import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-line bg-slate-100/70 px-4 py-3 text-sm text-slate-900 placeholder-zinc-500 outline-none focus:border-blaze";
const labelCls = "mb-1.5 block text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500";

const F = ({ label, children }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

const TRUCKS = ["4 Tonne Truck", "6 Tonne Truck", "8 Tonne Truck", "10 Tonne Truck", "12 Tonne Truck"];

export const AddJobModal = ({ onClose, onAdded, initial = {}, isOwner = true }) => {
  const [contractors, setContractors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [sendConf, setSendConf] = useState(true);
  const [fee, setFee] = useState("");
  const [feeMethod, setFeeMethod] = useState("skip");
  const [form, setForm] = useState({
    job_id: `YM-${Math.floor(10000 + Math.random() * 89999)}`,
    first_name: "", last_name: "", mobile: "", email: "",
    pickup_suburb: "", pickup_address: "", drop_suburb: "", drop_address: "",
    moving_date: "", start_time: "8:00 AM", bedrooms: "", property_type: "House",
    truck: "4 Tonne Truck", movers: 2, callout_hours: "", estimated_price: 0, rate: "", notes: "",
    contractor_id: "",
    ...initial,
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (isOwner) axios.get(`${API}/contractors`).then(({ data }) => setContractors(data || [])).catch(() => {});
    axios.get(`${API}/customers/lookup`).then(({ data }) => setCustomers(data || [])).catch(() => {});
  }, [isOwner]);

  const custMatches = (() => {
    const q = (form.first_name || "").trim().toLowerCase();
    if (!q) return [];
    return customers
      .filter((c) => c.name.toLowerCase().includes(q) || (c.mobile || "").includes(q) || (c.email || "").toLowerCase().includes(q))
      .slice(0, 6);
  })();

  const pickCustomer = (c) => {
    setForm((f) => ({
      ...f,
      first_name: c.first_name || "",
      last_name: c.last_name || "",
      mobile: c.mobile || "",
      email: c.email || "",
      pickup_suburb: c.pickup_suburb || "",
      pickup_address: c.pickup_address || "",
      drop_suburb: c.drop_suburb || "",
      drop_address: c.drop_address || "",
    }));
    setShowSug(false);
  };

  const selectContractor = (id) =>
    setForm((f) => ({ ...f, contractor_id: f.contractor_id === id ? "" : id }));

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/bookings/manual`, {
        job_id: form.job_id,
        first_name: form.first_name, last_name: form.last_name,
        mobile: form.mobile, email: form.email,
        pickup_suburb: form.pickup_suburb, pickup_address: form.pickup_address,
        drop_suburb: form.drop_suburb, drop_address: form.drop_address,
        moving_date: form.moving_date, start_time: form.start_time,
        bedrooms: form.bedrooms, property_type: form.property_type,
        truck: form.truck, movers: parseInt(form.movers) || 2,
        callout_hours: parseFloat(form.callout_hours) || 0,
        estimated_price: parseFloat(form.estimated_price) || 0,
        rate: parseFloat(form.rate) || 0,
        notes: form.notes,
        send_confirmation: sendConf,
        booking_fee: parseFloat(fee) || 0,
        booking_fee_method: parseFloat(fee) > 0 ? feeMethod : "skip",
        origin_url: window.location.origin,
      });
      if (data.fee_checkout_url) window.open(data.fee_checkout_url, "_blank");
      const jid = data.job_id;
      if (form.contractor_id) {
        try {
          await axios.patch(`${API}/bookings/${jid}/assign-contractor`, { contractor_id: form.contractor_id });
        } catch {
          toast.warning(`Job ${jid} saved, but contractor assignment failed. Assign it from job details.`);
          onAdded();
          onClose();
          return;
        }
      }
      toast.success(`Job ${jid} saved${form.contractor_id ? " and contractor notified" : ""}${sendConf && form.email ? " + confirmation emailed to customer" : ""}${data.fee_link_sent ? " + booking fee link emailed" : ""}${data.fee_checkout_url ? " + card checkout opened for booking fee" : ""}. Edit it anytime from job details.`);
      onAdded();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add job.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70 p-5" onClick={onClose} data-testid="add-job-modal">
      <div className="mx-auto my-8 w-full max-w-2xl border border-line bg-coal p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display text-2xl font-semibold uppercase tracking-tight text-slate-900">Add Job <span className="text-blaze">Manually</span></h3>
            <p className="mt-1 text-xs text-slate-500">For jobs taken from another company. All fields optional — save a skeleton now, edit it later from the job's details.</p>
          </div>
          <button onClick={onClose} className="p-1 text-slate-600 hover:text-slate-900" data-testid="add-job-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <F label="Job ID"><input className={`${inputCls} font-display tracking-wider`} value={form.job_id} onChange={set("job_id")} data-testid="aj-job-id" /></F>
          <F label="Moving Date"><input type="date" className={`${inputCls} text-slate-700`} value={form.moving_date} onChange={set("moving_date")} data-testid="aj-date" /></F>
          <F label="Customer First Name">
            <div className="relative">
              <input
                className={inputCls}
                value={form.first_name}
                onChange={(e) => { setForm({ ...form, first_name: e.target.value }); setShowSug(true); }}
                onFocus={() => setShowSug(true)}
                onBlur={() => setTimeout(() => setShowSug(false), 150)}
                placeholder="Type to search existing customers…"
                autoComplete="off"
                data-testid="aj-first-name"
              />
              {showSug && custMatches.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-56 overflow-y-auto border border-line bg-white shadow-lg" data-testid="aj-customer-suggestions">
                  {custMatches.map((c, i) => (
                    <button
                      key={i}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); pickCustomer(c); }}
                      className="flex w-full flex-col items-start border-b border-line/60 px-3 py-2 text-left last:border-0 hover:bg-slate-50"
                      data-testid={`aj-customer-option-${i}`}
                    >
                      <span className="text-sm font-semibold text-slate-900">{c.name}</span>
                      <span className="text-[11px] text-slate-500">{[c.mobile, c.email, c.pickup_suburb].filter(Boolean).join(" · ")}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </F>
          <F label="Last Name"><input className={inputCls} value={form.last_name} onChange={set("last_name")} data-testid="aj-last-name" /></F>
          <F label="Mobile"><input className={inputCls} value={form.mobile} onChange={set("mobile")} data-testid="aj-mobile" /></F>
          <F label="Email"><input type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="aj-email" /></F>
          <F label="Pickup Suburb"><input className={inputCls} value={form.pickup_suburb} onChange={set("pickup_suburb")} data-testid="aj-pickup-suburb" /></F>
          <F label="Pickup Address"><input className={inputCls} value={form.pickup_address} onChange={set("pickup_address")} data-testid="aj-pickup-address" /></F>
          <F label="Drop-off Suburb"><input className={inputCls} value={form.drop_suburb} onChange={set("drop_suburb")} data-testid="aj-drop-suburb" /></F>
          <F label="Drop-off Address"><input className={inputCls} value={form.drop_address} onChange={set("drop_address")} data-testid="aj-drop-address" /></F>
          <F label="Start Time"><input className={inputCls} value={form.start_time} onChange={set("start_time")} data-testid="aj-start-time" /></F>
          <F label="Bedrooms"><input className={inputCls} placeholder="e.g. 3" value={form.bedrooms} onChange={set("bedrooms")} data-testid="aj-bedrooms" /></F>
          <F label="Truck">
            <select className={`${inputCls} appearance-none`} value={form.truck} onChange={set("truck")} data-testid="aj-truck">
              {TRUCKS.map((t) => <option key={t} value={t} className="bg-coal text-slate-900">{t}</option>)}
            </select>
          </F>
          <F label="Movers Needed"><input type="number" min="1" className={inputCls} value={form.movers} onChange={set("movers")} data-testid="aj-movers" /></F>
          <F label="Call-out (hours, optional)"><input type="number" step="0.25" min="0" placeholder="e.g. 0.5 — leave blank for none" className={inputCls} value={form.callout_hours} onChange={set("callout_hours")} data-testid="aj-callout-hours" /></F>
          <F label="Price ($)"><input type="number" step="0.01" className={inputCls} value={form.estimated_price} onChange={set("estimated_price")} data-testid="aj-price" /></F>
          <F label="Rate ($/hr)"><input type="number" step="1" min="0" placeholder="e.g. 120" className={inputCls} value={form.rate} onChange={set("rate")} data-testid="aj-rate" /></F>
          <div className="sm:col-span-2">
            <F label="Notes / Instructions"><textarea rows={2} className={`${inputCls} resize-none`} value={form.notes} onChange={set("notes")} data-testid="aj-notes" /></F>
          </div>
          {isOwner && (
          <div className="sm:col-span-2">
            <label className={labelCls}>Assign Contractor Now (they get notified immediately)</label>
            <div className="flex flex-wrap gap-2" data-testid="aj-assign-options">
              {contractors.map((c) => {
                const active = form.contractor_id === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => selectContractor(c.id)}
                    data-testid={`aj-assign-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`border px-4 py-2 text-xs font-semibold transition-all ${active ? "border-blaze bg-blaze text-white" : "border-line text-slate-700 hover:border-blaze/60"}`}
                  >
                    {c.name}
                  </button>
                );
              })}
              {contractors.length === 0 && <p className="text-xs text-slate-500">No contractors yet — add one from the Contractors tab.</p>}
            </div>
          </div>
          )}
        </div>

        <div className="mt-6 border border-line/60 p-4" data-testid="aj-fee-section">
          <label className={labelCls}>Booking fee (optional)</label>
          <div className="mt-2 grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Fee amount ($)</label>
              <input type="number" min="0" step="1" placeholder="e.g. 50" className={inputCls} value={fee} onChange={(e) => setFee(e.target.value)} data-testid="aj-fee-amount" />
            </div>
            <div>
              <label className={labelCls}>Fee option</label>
              <select className={inputCls} value={feeMethod} onChange={(e) => setFeeMethod(e.target.value)} data-testid="aj-fee-method">
                <option value="skip">Skip — no booking fee</option>
                <option value="link">Email payment link to customer</option>
                <option value="card">Pay by card now (Stripe)</option>
              </select>
            </div>
          </div>
          <p className="mt-2 text-[10px] text-slate-500">
            "Email payment link" adds a Pay Booking Fee button to the customer's confirmation email. "Pay by card now" opens a secure Stripe checkout you can complete or share with the customer.
          </p>
        </div>

        <div className="mt-6 border border-line/60 p-4" data-testid="aj-confirm-section">
          <label className={labelCls}>Send booking confirmation to customer</label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setSendConf(true)}
              data-testid="aj-confirm-yes"
              className={`border px-4 py-3 text-xs font-bold uppercase tracking-wider transition-all ${sendConf ? "border-blaze bg-blaze text-white" : "border-line text-slate-700 hover:border-blaze/60"}`}
            >
              Yes — Send Confirmation
            </button>
            <button
              type="button"
              onClick={() => setSendConf(false)}
              data-testid="aj-confirm-no"
              className={`border px-4 py-3 text-xs font-bold uppercase tracking-wider transition-all ${!sendConf ? "border-blaze bg-blaze text-white" : "border-line text-slate-700 hover:border-blaze/60"}`}
            >
              No — Do Not Send
            </button>
          </div>
          <p className="mt-2 text-[10px] text-slate-500">
            The confirmation is emailed to the customer email above. If no email is entered, nothing is sent either way.
          </p>
        </div>

        <button
          onClick={submit}
          disabled={saving}
          className="mt-8 flex w-full items-center justify-center gap-2 bg-blaze py-4 text-xs font-bold uppercase tracking-[0.2em] text-white disabled:opacity-50"
          data-testid="aj-submit-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Job"}
        </button>
      </div>
    </div>
  );
};
